import React, { useEffect, useRef, useState } from 'react';
import { useChat } from '../contexts/ChatContext';
import { ChatBubbleOvalLeftEllipsisIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';

const JobChatWidget = ({ jobId, jobTitle, isOpen, onClose }) => {
  const { conversations, messagesByConv, loadMessages, sendMessage, startConversation, setConversationOpen, unreadByConversation } = useChat();
  const [input, setInput] = useState('');
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const listRef = useRef(null);

  // Find existing conversation for this job
  const jobConversation = conversations?.find(conv => conv.job_id === jobId);
  const loadedConversationRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    
    if (jobConversation && loadedConversationRef.current !== jobConversation.id) {
      setActiveConversationId(jobConversation.id);
      loadedConversationRef.current = jobConversation.id;
      loadMessages(jobConversation.id);
      setConversationOpen(jobConversation.id, true);
    } else if (!jobConversation) {
      // Reset when no conversation exists for this job
      setActiveConversationId(null);
      loadedConversationRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, jobConversation?.id]); // Only depend on the ID, not the whole object or loadMessages

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messagesByConv, activeConversationId]);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    if (!activeConversationId) {
      // Start new conversation for this job
      setLoading(true);
      try {
        const result = await startConversation(`Chat about ${jobTitle}`, jobId);
        if (result?.success) {
          const newId = result.data.id;
          setActiveConversationId(newId);
          await loadMessages(newId);
        }
      } catch (error) {
        console.error('Failed to start conversation:', error);
      } finally {
        setLoading(false);
      }
    }

    if (activeConversationId) {
      const res = await sendMessage(activeConversationId, input.trim());
      if (res?.success) {
        setInput('');
        // Message is already displayed via optimistic update, socket will update with real ID
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ChatBubbleOvalLeftEllipsisIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Chat Support</h3>
              <p className="text-sm text-gray-600">Job: {jobTitle}</p>
            </div>
          </div>
          <button
            onClick={() => {
              if (activeConversationId) setConversationOpen(activeConversationId, false);
              onClose();
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={listRef}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (messagesByConv[activeConversationId] || []).length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <ChatBubbleOvalLeftEllipsisIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Start a conversation</p>
              <p className="text-sm">Ask questions about this job or get support</p>
            </div>
          ) : (
            (messagesByConv[activeConversationId] || []).map((m) => (
              <div key={m.id} className={`flex ${m.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}>
                <div className={`${m.sender_type === 'customer' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'} px-4 py-2 rounded-lg max-w-[80%]`}>
                  <div className="text-sm whitespace-pre-wrap break-words">{m.message_text}</div>
                  <div className="flex items-center justify-between mt-1">
                    {m.sender_type === 'admin' && (
                      <div className="text-[11px] text-gray-500 mr-3 truncate max-w-[60%]">
                        {m.admin_first_name || m.admin_last_name ? `${m.admin_first_name || ''} ${m.admin_last_name || ''}`.trim() : (m.admin_email || 'Admin')}
                      </div>
                    )}
                    <div className={`${m.sender_type === 'customer' ? 'text-blue-100' : 'text-gray-500'} text-xs ml-auto`}>
                      {formatLocalTime(m.sent_at)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to format timestamps (handles UTC timestamps properly)
function formatLocalTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    // Match admin portal behavior: rely on browser local timezone
    return d.toLocaleString();
  } catch (error) {
    return '';
  }
}

export default JobChatWidget;
